import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/api/axios-instance";
import { translate } from "@/i18n/translate";
import type {
  DistributionDays,
  DistributionsResponse,
  EvaluationSettingsResponse,
  EvaluationSettingsUpdate,
  SettingsHistoryResponse,
} from "./types";

/** Hand-written hooks of the evaluation settings API (not in the committed OpenAPI schema). */

const BASE = "/api/admin/evaluation-settings";

export const evaluationSettingsKeys = {
  all: ["evaluation-settings"] as const,
  settings: ["evaluation-settings", "settings"] as const,
  history: (limit: number) => ["evaluation-settings", "history", limit] as const,
  distributions: (days: DistributionDays, branchId: string | null) =>
    ["evaluation-settings", "distributions", days, branchId ?? "all"] as const,
};

export function useEvaluationSettings() {
  return useQuery({
    queryKey: evaluationSettingsKeys.settings,
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<EvaluationSettingsResponse>(BASE, { signal });
      return data;
    },
    staleTime: 10_000,
    retry: 1,
  });
}

export function useEvaluationSettingsHistory(limit = 50) {
  return useQuery({
    queryKey: evaluationSettingsKeys.history(limit),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<SettingsHistoryResponse>(`${BASE}/history`, {
        params: { limit },
        signal,
      });
      return data;
    },
    staleTime: 10_000,
    retry: 1,
  });
}

/** histograms of the thresholded metrics; the backend caches them ~60 s */
export function useEvaluationDistributions(days: DistributionDays, branchId: string | null) {
  return useQuery({
    queryKey: evaluationSettingsKeys.distributions(days, branchId),
    queryFn: async ({ signal }) => {
      const { data } = await axiosInstance.get<DistributionsResponse>(`${BASE}/distributions`, {
        params: { days, ...(branchId ? { branch_id: branchId } : {}) },
        signal,
      });
      return data;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useSaveEvaluationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: EvaluationSettingsUpdate) => {
      const { data } = await axiosInstance.put<EvaluationSettingsResponse>(BASE, body);
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(evaluationSettingsKeys.settings, data);
      queryClient.invalidateQueries({ queryKey: ["evaluation-settings", "history"] });
    },
  });
}

export type SaveError =
  /** someone else saved in between (optimistic lock) */
  | { kind: "conflict"; currentVersion: number | null; message: string }
  /** unknown key / wrong type / out of range */
  | { kind: "invalid"; key: string | null; message: string }
  | { kind: "other"; message: string };

type AxiosLike = {
  response?: { status?: number; data?: { error?: string; key?: string; current_version?: number } };
  message?: string;
};

export function classifySaveError(error: unknown): SaveError {
  const e = error as AxiosLike;
  const status = e?.response?.status;
  const data = e?.response?.data;
  const message = data?.error ?? e?.message ?? translate("evalSettings.error.unknown");
  if (status === 409) return { kind: "conflict", currentVersion: data?.current_version ?? null, message };
  if (status === 400) return { kind: "invalid", key: data?.key ?? null, message };
  return { kind: "other", message };
}

export const httpStatus = (error: unknown): number | undefined => (error as AxiosLike)?.response?.status;

export function errorMessage(error: unknown): string {
  const e = error as AxiosLike;
  return e?.response?.data?.error ?? e?.message ?? translate("evalSettings.error.unknown");
}
